// Team Setup: who reports to whom, territory, branch, employee code,
// designation, joining date. Drives the Manager / Territory / Branch filters
// and comparisons, and lets a manager see their own team's numbers.
import { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Pencil, Search, Users } from 'lucide-react';
import { Spinner } from '@/shared/components/ui/Spinner';
import { errText, teamApi, usePerfMutation, useTeam, type TeamRow } from '../api';
import { Chip, Dialog, Empty, Field, Initials, inputCls, Panel } from './ui';

const ROLE: Record<string, string> = { bde: 'BDE', field_executive: 'Field Executive', employee: 'Sales Executive', manager: 'Manager', team_lead: 'Team Lead', department_admin: 'Department Administrator', admin: 'Admin', superadmin: 'Super Admin' };

export function TeamTab() {
  const { data, isLoading } = useTeam();
  const [q, setQ] = useState('');
  const [edit, setEdit] = useState<TeamRow | null>(null);
  const rows = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (data?.rows || []).filter((r) => !t || [r.name, r.username, r.employeeCode, r.territory, r.branch, r.managerName, r.designation].some((x) => (x || '').toLowerCase().includes(t)));
  }, [data, q]);
  if (isLoading || !data) return <div className="flex justify-center py-16"><Spinner /></div>;
  if (!data.rows.length) return <Empty title="No sales employees" text="Only active people in the Sales or Field Executive department appear here." />;
  const missing = data.rows.filter((r) => !r.managerId || !r.territory).length;
  const territories = [...new Set(data.rows.map((r) => r.territory).filter(Boolean))].sort();
  const branches = [...new Set(data.rows.map((r) => r.branch).filter(Boolean))].sort();

  return (
    <div className="flex flex-col gap-4">
      {missing > 0 && data.canEdit && (
        <p className="rounded-xl bg-blue-50 px-4 py-2.5 text-[13px] text-blue-800">
          <b>{missing}</b> of {data.rows.length} people have no manager or territory yet. Set them so the Manager / Territory filters and team comparisons work, and so each manager can see their own team.
        </p>
      )}
      <Panel pad={false} title="Sales team" subtitle={`${data.rows.length} people · ${territories.length} territories · ${branches.length} branches`} icon={Users}
        right={(
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="w-52 rounded-lg border border-slate-200 py-1.5 pl-8 pr-3 text-[13px] outline-none focus:border-blue-500" />
          </div>
        )}>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead><tr className="bg-slate-50 text-left text-data-xs font-semibold uppercase tracking-wide text-slate-500">
              {['Employee', 'Code', 'Designation', 'Reports to', 'Territory', 'Branch', 'Joined', ''].map((h) => <th key={h} className="whitespace-nowrap px-4 py-2.5">{h}</th>)}
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/70">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-3">
                      <Initials name={r.name} />
                      <div><p className="font-semibold text-slate-900">{r.name}</p><p className="text-data-xs text-slate-500">{r.username} · {ROLE[r.role] || r.role} · {r.department}</p></div>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-slate-700">{r.employeeCode || <Dash />}</td>
                  <td className="px-4 py-2.5 text-slate-700">{r.designation || <Dash />}</td>
                  <td className="px-4 py-2.5">{r.managerName ? <Chip className="bg-violet-50 text-violet-700">{r.managerName}</Chip> : <Dash />}</td>
                  <td className="px-4 py-2.5">{r.territory ? <Chip className="bg-blue-50 text-blue-700">{r.territory}</Chip> : <Dash />}</td>
                  <td className="px-4 py-2.5 text-slate-700">{r.branch || <Dash />}</td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-slate-600">{r.joiningDate ? new Date(r.joiningDate).toLocaleDateString('en-IN') : <Dash />}</td>
                  <td className="px-4 py-2.5 text-right">{data.canEdit && <button type="button" className="btn-sp" onClick={() => setEdit(r)}><Pencil className="h-3.5 w-3.5" /> Edit</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
      {edit && <EditDialog row={edit} managers={data.managers} territories={territories} branches={branches} onClose={() => setEdit(null)} />}
    </div>
  );
}

const Dash = () => <span className="text-slate-300">—</span>;

function EditDialog({ row, managers, territories, branches, onClose }: {
  row: TeamRow; managers: Array<{ id: string; name: string; role: string }>; territories: string[]; branches: string[]; onClose: () => void;
}) {
  const [f, setF] = useState({
    employeeCode: row.employeeCode || '', designation: row.designation || '', managerId: row.managerId || '',
    territory: row.territory || '', branch: row.branch || '', joiningDate: row.joiningDate ? row.joiningDate.slice(0, 10) : '',
  });
  const save = usePerfMutation((body: typeof f) => teamApi.saveProfile(row.id, { ...body, joiningDate: body.joiningDate || null }));
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((p) => ({ ...p, [k]: e.target.value }));
  return (
    <Dialog open onClose={onClose} title={row.name} subtitle={`${row.username} · ${row.department}`}
      footer={(
        <>
          <button type="button" className="btn-sp" onClick={onClose}>Cancel</button>
          <button type="button" className="btn-sp-primary" disabled={save.isPending} onClick={async () => {
            try { await save.mutateAsync(f); toast.success(`${row.name} updated.`); onClose(); } catch (err) { toast.error(errText(err, 'Could not save.')); }
          }}>{save.isPending ? 'Saving…' : 'Save'}</button>
        </>
      )}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Employee code"><input className={inputCls} value={f.employeeCode} onChange={set('employeeCode')} placeholder="e.g. RBM-S-014" /></Field>
        <Field label="Designation"><input className={inputCls} value={f.designation} onChange={set('designation')} placeholder="e.g. Sales Executive" /></Field>
        <Field label="Reports to (manager)" hint="The manager sees this person's numbers and approves their incentive.">
          <select className={inputCls} value={f.managerId} onChange={set('managerId')}>
            <option value="">— nobody —</option>
            {managers.filter((m) => m.id !== row.id).map((m) => <option key={m.id} value={m.id}>{m.name} ({ROLE[m.role] || m.role})</option>)}
          </select>
        </Field>
        <Field label="Joining date"><input className={inputCls} type="date" value={f.joiningDate} onChange={set('joiningDate')} /></Field>
        <Field label="Territory">
          <input className={inputCls} list="sp-territories" value={f.territory} onChange={set('territory')} placeholder="e.g. Mumbai West" />
          <datalist id="sp-territories">{territories.map((t) => <option key={t} value={t} />)}</datalist>
        </Field>
        <Field label="Branch">
          <input className={inputCls} list="sp-branches" value={f.branch} onChange={set('branch')} placeholder="e.g. Head Office" />
          <datalist id="sp-branches">{branches.map((t) => <option key={t} value={t} />)}</datalist>
        </Field>
      </div>
    </Dialog>
  );
}
